import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Pressable,
  Image,
  ActivityIndicator,
  RefreshControl,
  Modal,
  ScrollView,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import { useNavigation, useRoute } from '@react-navigation/native';
import colors from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { useNavigationVisibility } from '../context/NavigationVisibilityContext';
import api from '../services/api';

// Default icons for missing photos
const BRIDE_ICON = 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
const GROOM_ICON = 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png';

const SharedProfilesScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const { handleScroll } = useNavigationVisibility();
  // Use memberId from route params if available (mediator viewing member's shared profiles), otherwise use user's memberId (member viewing own shared profiles)
  const targetMemberId = route.params?.memberId || user?.memberId;
  const [profiles, setProfiles] = useState([]);
  const [memberProfile, setMemberProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeSort, setActiveSort] = useState('Newest');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [respondingShareId, setRespondingShareId] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);

  const filterOptions = ['All', 'Pending', 'Interested', 'NotInterested'];
  const moreFilterOptions = ['ShortListed', 'OnHold', 'Expired'];
  const allFilterOptions = [...filterOptions, ...moreFilterOptions];
  
  const sortOptions = [
    { key: 'Newest', label: 'Newest First' },
    { key: 'Oldest', label: 'Oldest First' },
    { key: 'NameAZ', label: 'Name A-Z' },
    { key: 'NameZA', label: 'Name Z-A' },
    { key: 'AgeAsc', label: 'Age: Low to High' },
    { key: 'AgeDesc', label: 'Age: High to Low' },
  ];

  useEffect(() => {
    setMemberProfile(null); // Reset member profile when viewing different member
    loadSharedProfiles();
  }, [targetMemberId]);

  const loadSharedProfiles = async () => {
    setIsLoading(true);
    try {
      const memberId = targetMemberId;
      const mediatorId = user?.mediatorId || '';
      
      console.log('Loading shared profiles for member:', memberId, 'mediator:', mediatorId);
      
      if (!memberId) {
        console.log('No member ID found');
        setProfiles([]);
        return;
      }

      // First, load member profile to get gender for shareType
      let shareType = '';
      if (!memberProfile) {
        try {
          const profileResponse = await api.get(`/api/EnhancedMember/shortProfile/${memberId}`);
          const profileData = profileResponse.data?.data || profileResponse.data;
          setMemberProfile(profileData);
          
          // Determine shareType based on member's gender
          // If member is Female (Bride), Groom profiles are shared to them -> GroomToBride
          // If member is Male (Groom), Bride profiles are shared to them -> BrideToGroom
          shareType = profileData.gender === 'Female' ? 'GroomToBride' : 'BrideToGroom';
        } catch (error) {
          console.error('Error loading member profile:', error);
        }
      } else {
        shareType = memberProfile.gender === 'Female' ? 'GroomToBride' : 'BrideToGroom';
      }

      console.log('Member gender:', memberProfile?.gender || 'unknown', 'ShareType:', shareType);

      const response = await api.get(`/api/Share/shared/enhanced?mediatorId=${mediatorId}&memberId=${memberId}&pageSize=1000&pageNumber=1&shareType=${shareType}`);
      console.log('Shared profiles API response:', response.data);
      
      // Handle different response structures
      let profilesData = [];
      
      if (response.data) {
        // Check if data is in response.data.data
        if (response.data.data && Array.isArray(response.data.data)) {
          profilesData = response.data.data;
        }
        // Check if data is in response.data.members
        else if (response.data.members && Array.isArray(response.data.members)) {
          profilesData = response.data.members;
        }
        // Check if data is in response.data.profiles
        else if (response.data.profiles && Array.isArray(response.data.profiles)) {
          profilesData = response.data.profiles;
        }
        // Check if response.data itself is an array
        else if (Array.isArray(response.data)) {
          profilesData = response.data;
        }
      }
      
      console.log('Parsed profiles data:', profilesData);
      console.log('Profiles count:', profilesData.length);
      
      setProfiles(profilesData);
    } catch (error) {
      console.error('Error loading shared profiles:', error);
      console.error('Error details:', error.response?.data);
      setProfiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadSharedProfiles();
    setRefreshing(false);
  };

  const handleShareResponse = async (shareId, memberId, responseType, responseNotes = '') => {
    setRespondingShareId(shareId);
    try {
      const payload = {
        shareId: shareId,
        memberId: memberId,
        responseType: responseType,
        responseNotes: responseNotes
      };

      console.log('Sending share response:', payload);
      const response = await api.post('/api/Share/respond', payload);
      console.log('Share response successful:', response.data);
      
      // Reload profiles to get updated data
      await loadSharedProfiles();
    } catch (error) {
      console.error('Error submitting share response:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setRespondingShareId(null);
    }
  };

  // Get the response type for a profile (from any share's active response)
  const getProfileResponseType = (profile) => {
    const shares = profile.shares || [];
    for (const share of shares) {
      const responses = share.responses || [];
      const activeResponse = responses.find(r => r.isActive === true);
      if (activeResponse) {
        return activeResponse.responseType;
      }
    }
    return null; // No response = Pending
  };

  // Calculate filter counts
  const filterCounts = useMemo(() => {
    const counts = {
      All: profiles.length,
      Pending: 0,
      Interested: 0,
      NotInterested: 0,
      ShortListed: 0,
      OnHold: 0,
      Expired: 0,
    };

    profiles.forEach(profile => {
      const responseType = getProfileResponseType(profile);
      if (!responseType) {
        counts.Pending++;
      } else if (counts[responseType] !== undefined) {
        counts[responseType]++;
      }
    });

    return counts;
  }, [profiles]);

  // Filter profiles based on active filter
  const filteredProfiles = useMemo(() => {
    let result = profiles;
    
    // Apply filter
    if (activeFilter !== 'All') {
      result = profiles.filter(profile => {
        const responseType = getProfileResponseType(profile);
        if (activeFilter === 'Pending') {
          return !responseType;
        }
        return responseType === activeFilter;
      });
    }

    // Apply sort
    result = [...result].sort((a, b) => {
      switch (activeSort) {
        case 'Newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'Oldest':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'NameAZ':
          return `${a.name} ${a.surName}`.localeCompare(`${b.name} ${b.surName}`);
        case 'NameZA':
          return `${b.name} ${b.surName}`.localeCompare(`${a.name} ${a.surName}`);
        case 'AgeAsc':
          return new Date(b.dateOfBirth || 0) - new Date(a.dateOfBirth || 0);
        case 'AgeDesc':
          return new Date(a.dateOfBirth || 0) - new Date(b.dateOfBirth || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [profiles, activeFilter, activeSort]);

  const getFilterLabel = (filter) => {
    const labels = {
      All: 'All',
      Pending: 'Pending',
      Interested: 'Interested',
      NotInterested: 'Not Interested',
      ShortListed: 'ShortListed',
      OnHold: 'On Hold',
      Expired: 'Expired',
    };
    return labels[filter] || filter;
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    // Handle different date formats
    let birthDate;
    if (typeof dateOfBirth === 'string') {
      // Try parsing as ISO string or other formats
      birthDate = new Date(dateOfBirth);
    } else {
      birthDate = new Date(dateOfBirth);
    }
    
    // Validate date
    if (isNaN(birthDate.getTime())) return 'N/A';
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 && age < 120 ? age : 'N/A';
  };

  const handleProfilePress = (memberId) => {
    navigation.navigate('ProfileDetail', { memberId, isSharedProfile: true, returnScreen: 'SharedProfiles' });
  };

  const renderShareResponseIcon = (share, allShares) => {
    const isBrideToGroom = share.shareType === 'BrideToGroom';
    const iconUrl = isBrideToGroom 
      ? 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png'
      : 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
    
    // Find active response from responses array
    const responses = share.responses || [];
    const activeResponse = responses.find(r => r.isActive === true);
    
    let responseIcon = null;
    let responseColor = colors.textSecondary;
    
    if (activeResponse) {
      if (activeResponse.responseType === 'NotInterested') {
        responseIcon = 'thumbs-down';
        responseColor = '#FF4444';
      } else if (activeResponse.responseType === 'Interested') {
        responseIcon = 'thumbs-up';
        responseColor = '#00D9B5';
      }
    }
    
    return (
      <TouchableOpacity 
        style={styles.shareResponseItem} 
        key={share.shareId}
        onPress={(e) => {
          e.stopPropagation();
          // Combine all responses from all shares
          const allResponses = allShares.flatMap(s => s.responses || []);
          setSelectedShare({ ...share, responses: allResponses });
          setShowResponseModal(true);
        }}
      >
        <View style={styles.shareIconContainer}>
          <Image source={{ uri: iconUrl }} style={styles.shareTypeIcon} />
          {responseIcon ? (
            <View style={[styles.responseIndicator, { backgroundColor: responseColor }]}>
              <SvgIcon name={responseIcon} size={10} color={colors.white} />
            </View>
          ) : (
            <View style={[styles.responseIndicator, { backgroundColor: '#FFA500' }]}>
              <SvgIcon name="hourglass-outline" size={10} color={colors.white} />
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderProfileCard = ({ item }) => {
    const shares = item.shares || [];
    const primaryShare = shares[0]; // Use first share for action buttons
    
    // Find active response
    const responses = primaryShare?.responses || [];
    const activeResponse = responses.find(r => r.isActive === true);
    const activeResponseType = activeResponse?.responseType;
    
    return (
      <TouchableOpacity
        style={styles.profileCard}
        onPress={() => handleProfilePress(item.memberId)}
        activeOpacity={0.7}
      >
        <View style={styles.cardBody}>
          {/* Left 50% - Photo */}
          <View style={styles.photoSection}>
            <Image
              source={{ uri: item.topPhotoSecuredUrl || (item.gender === 'Male' ? GROOM_ICON : BRIDE_ICON) }}
              style={styles.profileImage}
            />
            {item.isApproved && (
              <View style={styles.verifiedBadge}>
                <SvgIcon name="checkmark-circle" size={20} color={colors.success} />
              </View>
            )}
            
            {/* Share Response Icons at bottom of photo */}
            {shares.length > 0 && (
              <View style={styles.shareResponsePhotoContainer}>
                {shares.map(share => renderShareResponseIcon(share, shares))}
              </View>
            )}
          </View>

          {/* Right 50% - Name and Details */}
          <View style={styles.detailsSection}>
            <View style={styles.nameContainer}>
              <Text style={styles.profileName} numberOfLines={2}>
                {item.name} {item.surName}
              </Text>
              {item.isApproved && (
                <SvgIcon name="checkmark-circle" size={16} color={colors.success} style={styles.verifiedIcon} />
              )}
            </View>

            <View style={styles.infoRow}>
              <SvgIcon name="calendar-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText}>{item.age || 'N/A'} years</Text>
            </View>
            <View style={styles.infoRow}>
              <SvgIcon name="location-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={1}>{item.city}</Text>
            </View>
            <View style={styles.infoRow}>
              <SvgIcon name="briefcase-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={1}>{item.profession || 'Not specified'}</Text>
            </View>
            <View style={styles.infoRow}>
              <SvgIcon name="school-outline" size={16} color={colors.accent} />
              <Text style={styles.infoText} numberOfLines={2}>
                {item.highestEducationQualification || 'Not specified'}
              </Text>
            </View>

            {/* Action Buttons - Only show for members viewing their own shared profiles */}
            {primaryShare && user?.userType === 'Member' && user?.memberId === targetMemberId && (
              <View style={styles.actionButtonsContainer}>
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.actionButton,
                    styles.onHoldButton,
                    (pressed || hovered || activeResponseType === 'OnHold') && styles.onHoldButtonActive
                  ]}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleShareResponse(primaryShare.shareId, user.memberId, 'OnHold', '');
                  }}
                  disabled={respondingShareId === primaryShare.shareId}
                >
                  <SvgIcon name="pause" size={16} color={colors.white} />
                </Pressable>
                
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.actionButton,
                    styles.shortlistButton,
                    (pressed || hovered || activeResponseType === 'Shortlisted') && styles.shortlistButtonActive
                  ]}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleShareResponse(primaryShare.shareId, user.memberId, 'Shortlisted', '');
                  }}
                  disabled={respondingShareId === primaryShare.shareId}
                >
                  <SvgIcon name="star" size={16} color={colors.white} />
                </Pressable>
                
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.actionButton,
                    styles.dislikeButton,
                    (pressed || hovered || activeResponseType === 'NotInterested') && styles.dislikeButtonActive
                  ]}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleShareResponse(primaryShare.shareId, user.memberId, 'NotInterested', 'NotInterested');
                  }}
                  disabled={respondingShareId === primaryShare.shareId}
                >
                  <SvgIcon name="thumbs-down" size={16} color={colors.white} />
                </Pressable>
                
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.actionButton,
                    styles.likeButton,
                    (pressed || hovered || activeResponseType === 'Interested') && styles.likeButtonActive
                  ]}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleShareResponse(primaryShare.shareId, user.memberId, 'Interested', 'Interested');
                  }}
                  disabled={respondingShareId === primaryShare.shareId}
                >
                  <SvgIcon name="thumbs-up" size={16} color={colors.white} />
                </Pressable>
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={styles.loadingText}>Loading shared profiles...</Text>
      </View>
    );
  }

  if (profiles.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <SvgIcon name="people-outline" size={80} color={colors.textSecondary} />
        <Text style={styles.emptyText}>No profiles shared with you yet</Text>
        <Text style={styles.emptySubtext}>
          Your mediator will share suitable profiles here
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Filter and Sort Row */}
      <View style={styles.filterSortContainer}>
        {/* Filter Dropdown */}
        <View style={styles.dropdownWrapper}>
          <TouchableOpacity
            style={styles.dropdownButton}
            onPress={() => {
              setShowFilterDropdown(!showFilterDropdown);
              setShowSortDropdown(false);
            }}
          >
            <SvgIcon name="filter-outline" size={18} color={colors.accent} />
            <Text style={styles.dropdownButtonText}>
              {getFilterLabel(activeFilter)} ({filterCounts[activeFilter]})
            </Text>
            <SvgIcon 
              name={showFilterDropdown ? "chevron-up" : "chevron-down"} 
              size={16} 
              color={colors.white} 
            />
          </TouchableOpacity>
          
          {showFilterDropdown && (
            <View style={[styles.dropdown, styles.filterDropdown]}>
              {allFilterOptions.map(filter => (
                <TouchableOpacity
                  key={filter}
                  style={[
                    styles.dropdownItem,
                    activeFilter === filter && styles.activeDropdownItem
                  ]}
                  onPress={() => {
                    setActiveFilter(filter);
                    setShowFilterDropdown(false);
                  }}
                >
                  <Text style={[
                    styles.dropdownItemText,
                    activeFilter === filter && styles.activeDropdownItemText
                  ]}>
                    {getFilterLabel(filter)}
                  </Text>
                  <Text style={[
                    styles.dropdownItemCount,
                    activeFilter === filter && styles.activeDropdownItemText
                  ]}>
                    {filterCounts[filter]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Sort Dropdown */}
        <View style={styles.dropdownWrapper}>
          <TouchableOpacity
            style={styles.dropdownButton}
            onPress={() => {
              setShowSortDropdown(!showSortDropdown);
              setShowFilterDropdown(false);
            }}
          >
            <SvgIcon name="swap-vertical-outline" size={18} color={colors.accent} />
            <Text style={styles.dropdownButtonText}>
              {sortOptions.find(s => s.key === activeSort)?.label || 'Sort'}
            </Text>
            <SvgIcon 
              name={showSortDropdown ? "chevron-up" : "chevron-down"} 
              size={16} 
              color={colors.white} 
            />
          </TouchableOpacity>
          
          {showSortDropdown && (
            <View style={[styles.dropdown, styles.sortDropdown]}>
              {sortOptions.map(sort => (
                <TouchableOpacity
                  key={sort.key}
                  style={[
                    styles.dropdownItem,
                    activeSort === sort.key && styles.activeDropdownItem
                  ]}
                  onPress={() => {
                    setActiveSort(sort.key);
                    setShowSortDropdown(false);
                  }}
                >
                  <Text style={[
                    styles.dropdownItemText,
                    activeSort === sort.key && styles.activeDropdownItemText
                  ]}>
                    {sort.label}
                  </Text>
                  {activeSort === sort.key && (
                    <SvgIcon name="checkmark" size={16} color={colors.primary} />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>

      {/* Profile Count with Back Button */}
      <View style={styles.countContainer}>
        <TouchableOpacity 
          style={styles.backButtonInline}
          onPress={() => navigation.goBack()}
        >
          <SvgIcon name="arrow-back" size={20} color={colors.white} />
        </TouchableOpacity>
        <Text style={styles.countText}>
          Showing {filteredProfiles.length} of {profiles.length} profiles
        </Text>
      </View>

      <FlatList
        data={filteredProfiles}
        renderItem={renderProfileCard}
        keyExtractor={(item) => item.memberId?.toString()}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.accent}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyFilterContainer}>
            <SvgIcon name="filter-outline" size={60} color={colors.textSecondary} />
            <Text style={styles.emptyFilterText}>No profiles match this filter</Text>
          </View>
        }
      />

      {/* Response Modal */}
      <Modal
        visible={showResponseModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowResponseModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Profile Comments</Text>
              <TouchableOpacity onPress={() => setShowResponseModal(false)}>
                <SvgIcon name="close" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              {selectedShare?.responses && selectedShare.responses.length > 0 ? (
                selectedShare.responses.map((response, index) => {
                  const responseDate = new Date(response.responseDate);
                  const formattedDate = responseDate.toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }).replace(',', '');

                  let badgeColor = '#6C757D';
                  let badgeLabel = response.responseType;
                  
                  if (response.responseType === 'Interested') {
                    badgeColor = '#00D9B5';
                    badgeLabel = 'Interested';
                  } else if (response.responseType === 'NotInterested') {
                    badgeColor = '#FF4444';
                    badgeLabel = 'NotInterested';
                  } else if (response.responseType === 'Shortlisted') {
                    badgeColor = '#8A2BE2';
                    badgeLabel = 'Shortlisted';
                  } else if (response.responseType === 'OnHold') {
                    badgeColor = '#FFC107';
                    badgeLabel = 'OnHold';
                  }

                  return (
                    <View key={index} style={styles.responseItem}>
                      <View style={styles.responseHeader}>
                        <View style={[styles.responseBadge, { backgroundColor: badgeColor }]}>
                          <Text style={styles.responseBadgeText}>{badgeLabel}</Text>
                        </View>
                        <Text style={styles.responseDate}>{formattedDate}</Text>
                      </View>
                      <Text style={styles.responseText}>
                        <Text style={styles.responseName}>
                          {response.respondingMemberName || 'User'}: 
                        </Text>
                        <Text style={styles.responseNotes}>"{response.responseNotes || response.responseType}"</Text>
                      </Text>
                    </View>
                  );
                })
              ) : (
                <View style={styles.noResponseContainer}>
                  <SvgIcon name="hourglass-outline" size={48} color={colors.textSecondary} />
                  <Text style={styles.noResponseText}>No Response Yet</Text>
                  <Text style={styles.noResponseSubtext}>This profile has not responded to the share.</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  backButtonInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '500',
  },
  filterSortContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1A5A6B',
    zIndex: 100,
  },
  dropdownWrapper: {
    flex: 1,
    position: 'relative',
    zIndex: 100,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#1A5A6B',
    gap: 6,
  },
  dropdownButtonText: {
    flex: 1,
    color: colors.white,
    fontSize: 13,
    fontWeight: '500',
  },
  dropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: colors.primary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.accent,
    paddingVertical: 4,
    zIndex: 1000,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
  filterDropdown: {
    minWidth: 180,
  },
  sortDropdown: {
    minWidth: 160,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  activeDropdownItem: {
    backgroundColor: colors.accent,
  },
  dropdownItemText: {
    color: colors.white,
    fontSize: 14,
  },
  activeDropdownItemText: {
    color: colors.primary,
    fontWeight: '600',
  },
  dropdownItemCount: {
    color: colors.accent,
    fontSize: 13,
    fontWeight: '600',
  },
  countContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 8,
    backgroundColor: 'rgba(0, 217, 181, 0.1)',
  },
  countText: {
    color: colors.white,
    fontSize: 13,
    textAlign: 'center',
    flex: 1,
  },
  emptyFilterContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyFilterText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: 15,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  loadingText: {
    color: colors.white,
    marginTop: 10,
    fontSize: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
    padding: 40,
  },
  emptyText: {
    fontSize: 18,
    color: colors.white,
    marginTop: 20,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 10,
    textAlign: 'center',
  },
  listContent: {
    padding: 15,
  },
  profileCard: {
    backgroundColor: '#0F3D4A',
    borderRadius: 12,
    marginBottom: 15,
    overflow: 'hidden',
  },
  cardBody: {
    flexDirection: 'row',
    height: 220,
  },
  photoSection: {
    width: '50%',
    position: 'relative',
  },
  profileImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  verifiedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 3,
  },
  shareResponsePhotoContainer: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    flexDirection: 'row',
    gap: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 6,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    justifyContent: 'center',
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  likeButton: {
    borderColor: '#00D9B5',
  },
  likeButtonActive: {
    backgroundColor: '#00D9B5',
  },
  shortlistButton: {
    borderColor: '#8A2BE2',
  },
  shortlistButtonActive: {
    backgroundColor: '#8A2BE2',
  },
  onHoldButton: {
    borderColor: '#FFC107',
  },
  onHoldButtonActive: {
    backgroundColor: '#FFC107',
  },
  dislikeButton: {
    borderColor: '#FF4444',
  },
  dislikeButtonActive: {
    backgroundColor: '#FF4444',
  },
  detailsSection: {
    width: '50%',
    padding: 12,
    justifyContent: 'space-between',
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 4,
  },
  profileName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.white,
    flex: 1,
  },
  verifiedIcon: {
    marginTop: 2,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 6,
  },
  infoText: {
    color: '#E0E0E0',
    fontSize: 13,
    flex: 1,
  },
  shareResponseContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 6,
  },
  shareResponseItem: {
    alignItems: 'center',
  },
  shareIconContainer: {
    position: 'relative',
  },
  shareTypeIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: colors.white,
  },
  responseIndicator: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.white,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1A2332',
    borderRadius: 12,
    width: '90%',
    maxWidth: 400,
    maxHeight: '70%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#00A9A0',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.white,
  },
  modalBody: {
    padding: 16,
  },
  responseItem: {
    backgroundColor: '#0D1620',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  responseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  responseBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
  },
  responseBadgeText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '600',
  },
  responseDate: {
    color: '#8E9DAD',
    fontSize: 12,
  },
  responseText: {
    color: colors.white,
    fontSize: 14,
    lineHeight: 20,
  },
  responseName: {
    fontWeight: '600',
    color: colors.white,
  },
  responseNotes: {
    fontStyle: 'italic',
    color: '#E0E0E0',
  },
  noResponseContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  noResponseText: {
    fontSize: 18,
    color: colors.white,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  noResponseSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
});

export default SharedProfilesScreen;
